import type { AppView } from '@/app/router/registry';
import type { ModuleAccess } from '@/data-sources/contract';

/** Whether the signed-in person may open a sidebar module (`bootstrap.modules`). */
export function canOpen(modules: ModuleAccess, navItem: string | undefined): boolean {
  if (modules === 'all' || navItem === undefined) return true;
  return modules.includes(navItem);
}

/** The sidebar items to draw: the tree's pages the person may open. */
export function openNavItems(
  visible: ReadonlySet<string>,
  modules: ModuleAccess,
): ReadonlySet<string> {
  if (modules === 'all') return visible;
  return new Set([...visible].filter((id) => modules.includes(id)));
}

/**
 * Modules a deployed build opens only to named clients, by workspace id (the
 * v2 team id). deepdive-v2 keeps Influencers to GP in production
 * (`VITE_INFLUENCERS_CLIENT_IDS`); v3 does the same on staging too, which
 * clients use (the user's call, 2026-10-26). For every other workspace the
 * module is not there: no sidebar row, and its pages are not found. The dev
 * server and `/test` open everything — see `DashboardLayout`.
 *
 * In code rather than in the container's env because Terraform owns that env;
 * adding a client is a deploy, as it was in v2.
 */
export const CLIENT_ONLY_MODULES: Readonly<Record<string, readonly string[]>> = {
  influencers: [
    '883ae739-0959-421d-b9d6-21caec4e6b55', // GP
  ],
};

/** Whether a sidebar module is released to this workspace — see `CLIENT_ONLY_MODULES`. */
export function isReleased(navItem: string | undefined, workspaceId: string): boolean {
  const clients = navItem === undefined ? undefined : CLIENT_ONLY_MODULES[navItem];
  return clients === undefined || clients.includes(workspaceId);
}

/** The sidebar items released to this workspace. */
export function releasedNavItems(
  visible: ReadonlySet<string>,
  workspaceId: string,
): ReadonlySet<string> {
  return new Set([...visible].filter((id) => isReleased(id, workspaceId)));
}

/**
 * The people who see the executive view and nothing else, by v2 user id:
 * `bootstrap.session.user.id`, the profile's `session.sub` (`"user".id` in
 * v2's database). Any other page sends them to the executive dashboard, and
 * their menu loses Settings and Invite member. In a deployed live build the
 * executive view is theirs alone — anyone else finds its pages not found; the
 * dev server and `/test` open it to everyone, as they open `CLIENT_ONLY_MODULES`.
 *
 * Their v2 account is a USER with the `overview`, `sentiment`, `competitions`
 * and `community_and_influencer` modules: v2 lets a USER read an endpoint with
 * any one of the modules it names, and the executive cards read endpoints that
 * name only `sentiment` (brand health, mentions, topic ownership), only
 * `competitions` (mention volume, positive share, page performance) or only
 * `community_and_influencer` (Top voices, for each operator's authors — without
 * it the operator ranking just has none). An ADMIN passes every guard, but is
 * then an admin wherever it signs in, v2's own app included. In code for the
 * reason `CLIENT_ONLY_MODULES` is: adding someone is a deploy.
 */
export const EXECUTIVE_USERS: readonly string[] = [
  'c7b73bc1-c14e-494d-baa5-70e103ddd770', // GP's executive account (2026-10-27)
];

export const isExecutive = (userId: string, executives: readonly string[] = EXECUTIVE_USERS) =>
  executives.includes(userId);

/** What a page is to the person reaching it — see `viewAccess`. */
export type ViewAccess = 'open' | 'not-found' | 'to-executive';

/**
 * An executive is sent to the executive view from anywhere outside it: an
 * analyst page, or a path that matches no page (`view` undefined). Anyone else
 * does not find the executive view in a deployed build (`gated`).
 */
export function viewAccess(
  view: AppView | undefined,
  { executive, gated }: { executive: boolean; gated: boolean },
): ViewAccess {
  if (executive) return view === 'executive' ? 'open' : 'to-executive';
  return gated && view === 'executive' ? 'not-found' : 'open';
}
